import React, { useState, useEffect, useRef } from 'react';
import { Box, Button, Container, Slider, TextField, Typography, Paper, FormControl, InputLabel, Select, MenuItem, CircularProgress, Fade, InputAdornment, Snackbar, Alert, List, ListItem, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import AssessmentIcon from '@mui/icons-material/Assessment';
import RefreshIcon from '@mui/icons-material/Refresh';
import api from '../../services/api';
import FlexCardPrompt from '../flex/FlexCardPrompt';

// Styled components
const BlurredText = styled(Box)(({ isBlurred, theme }) => ({
  filter: isBlurred ? 'blur(5px)' : 'none',
  transition: 'filter 0.3s ease',
  position: 'relative',
  height: '30vh',
  padding: '20px',
  backgroundColor: theme.palette.background.paper,
  borderRadius: '12px',
  marginTop: '20px',
  marginBottom: '20px',
  overflowWrap: 'anywhere',
  maxWidth: '100%',
  overflowY: 'auto',
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: theme.palette.mode === 'dark' ? '0 4px 12px rgba(0, 0, 0, 0.3)' : '0 4px 12px rgba(0, 0, 0, 0.05)',
  '&::-webkit-scrollbar': {
    width: '0px',
    background: 'transparent'
  },
  scrollbarWidth: 'none', /* Firefox */
  msOverflowStyle: 'none', /* IE and Edge */
  scrollBehavior: 'smooth',
  overscrollBehavior: 'contain',
  willChange: 'scroll-position',
}));

const TextContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  lineHeight: '1.8',
  fontSize: '18px',
  padding: '20px',
  textAlign: 'left',
  '& .word': {
    display: 'inline',
    whiteSpace: 'nowrap',
    transition: 'color 0.15s ease',
    padding: '0 1px'
  },
  '& .space': {
    display: 'inline',
    whiteSpace: 'normal'
  },
  fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  color: theme.palette.text.primary,
  letterSpacing: '0.01em'
}));

const ReadingGuide = styled(Box)({
  position: 'absolute',
  width: '5px',
  height: '5px',
  borderRadius: '50%',
  backgroundColor: '#00BFFF',
  transform: 'translate(0, 0)',
  pointerEvents: 'none',
  zIndex: 10,
  display: 'none',
  transition: 'transform 0.15s cubic-bezier(0.4, 0.0, 0.2, 1)',
  boxShadow: '0 0 4px rgba(0, 191, 255, 0.5)',
  opacity: 0.9,
  willChange: 'transform',
});

const CountdownOverlay = styled(Box)({
  position: 'fixed',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  zIndex: 1000,
  backgroundColor: 'rgba(0, 0, 0, 0.7)',
  color: 'white',
  padding: '2rem',
  borderRadius: '50%',
  width: '150px',
  height: '150px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
});

const InfoBar = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '1rem',
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 191, 255, 0.06)' : '#f5f5f5',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '8px',
  marginBottom: '1rem'
}));

const ChunkHighlight = styled('span')({
  backgroundColor: 'rgba(255, 235, 59, 0.3)',
  padding: '2px 4px',
  borderRadius: '4px',
});

const SpeedReader = () => {
  const [text, setText] = useState('');
  const [isBlurred, setIsBlurred] = useState(true);
  const [isReading, setIsReading] = useState(false);
  const [wpm, setWpm] = useState(300);
  const [dotSpacing, setDotSpacing] = useState(3);
  const [technique, setTechnique] = useState('both');
  const [category, setCategory] = useState('general');
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const [totalReadingTime, setTotalReadingTime] = useState(0);
  const [remainingTime, setRemainingTime] = useState(0);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [wordCount, setWordCount] = useState(0);
  const [showBadgeAlert, setShowBadgeAlert] = useState(false);
  const [earnedBadges, setEarnedBadges] = useState([]);
  const [sessionCompleted, setSessionCompleted] = useState(false);
  const [quizScore, setQuizScore] = useState(0);
  const [currentStats, setCurrentStats] = useState({});
  const [error, setError] = useState(''); // eslint-disable-line no-unused-vars
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [currentQuizQuestion, setCurrentQuizQuestion] = useState(0);
  const [quizAnswers, setQuizAnswers] = useState([]);
  const [quizInProgress, setQuizInProgress] = useState(false);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [isChrome, setIsChrome] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [difficultWords, setDifficultWords] = useState([]);
  const [pronunciationPaused, setPronunciationPaused] = useState(false);
  const [selectedWord, setSelectedWord] = useState(null);
  const [readingLevel, setReadingLevel] = useState('native');
  const [articleLanguage, setArticleLanguage] = useState('en');
  const [flexCardEligibility, setFlexCardEligibility] = useState(null);

  const readingLevels = [
    { value: 'grade1', label: '1st Grade' }, { value: 'grade2', label: '2nd Grade' },
    { value: 'grade3', label: '3rd Grade' }, { value: 'grade4', label: '4th Grade' },
    { value: 'grade5', label: '5th Grade' }, { value: 'grade6', label: '6th Grade' },
    { value: 'grade7', label: '7th Grade' }, { value: 'grade8', label: '8th Grade' },
    { value: 'grade9', label: '9th Grade' }, { value: 'grade10', label: '10th Grade' },
    { value: 'grade11', label: '11th Grade' }, { value: 'grade12', label: '12th Grade' },
    { value: 'college', label: 'College' }, { value: 'native', label: 'Native / No Limit' }
  ];

  const languages = [
    { value: 'en', label: 'English' }, { value: 'es', label: 'Spanish' },
    { value: 'fr', label: 'French' }, { value: 'de', label: 'German' },
    { value: 'pt', label: 'Portuguese' }, { value: 'it', label: 'Italian' },
    { value: 'nl', label: 'Dutch' }, { value: 'ja', label: 'Japanese' },
    { value: 'ko', label: 'Korean' }, { value: 'zh', label: 'Chinese' },
    { value: 'ar', label: 'Arabic' }, { value: 'hi', label: 'Hindi' },
    { value: 'ru', label: 'Russian' }, { value: 'tr', label: 'Turkish' },
    { value: 'pl', label: 'Polish' }, { value: 'sv', label: 'Swedish' }
  ];

  const textContainerRef = useRef(null);
  const readingGuideRef = useRef(null);
  const timerRef = useRef(null);
  const scrollAnimationRef = useRef(null);

  useEffect(() => {
    // Detect Chrome browser
    const isChromeBrowser = /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
    setIsChrome(isChromeBrowser);
    
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  const calculateReadingParams = (text, targetWpm) => {
    if (!text) return { dotSpacing: 2 };
    
    const words = text.split(/\s+/).filter(w => w.length > 0);
    const totalWords = words.length;
    const readingTimeMs = (totalWords / targetWpm) * 60 * 1000;
    
    // Calculate dot spacing based on WPM and technique
    let dotSpacing;
    if (technique === 'both') {
      // Only even values allowed for Guide Dot + Chunking (dot must center evenly)
      dotSpacing = targetWpm <= 500 ? 2 : 4;
    } else {
      if (targetWpm <= 300) {
        dotSpacing = 3;
      } else if (targetWpm <= 700) {
        dotSpacing = 4;
      } else if (targetWpm <= 1200) {
        dotSpacing = 5;
      } else {
        dotSpacing = 6;
      }
    }
    
    // Ensure we don't have too few dots
    dotSpacing = Math.min(dotSpacing, Math.ceil(totalWords / 15)); // At least 15 dots total
    if (technique === 'both') dotSpacing = dotSpacing < 3 ? 2 : 4; // Snap to valid even value
    
    console.log('Reading parameters:', {
      totalWords,
      readingTimeMs: Math.round(readingTimeMs),
      targetWpm,
      wordsPerSecond: Math.round((targetWpm / 60) * 100) / 100,
      dotSpacing,
      estimatedDots: Math.ceil(totalWords / dotSpacing),
      estimatedTimeMinutes: Math.round(readingTimeMs / 1000 / 60 * 100) / 100
    });

    return {
      dotSpacing,
      estimatedTime: readingTimeMs / 1000 / 60 // in minutes
    };
  };

  const startReading = () => {
    if (!text) return;
    
    const words = text.split(/\s+/).filter(w => w.length > 0);
    const totalTime = calculateReadingTime(words.length);
    const params = calculateReadingParams(text, wpm);
    
    setTotalReadingTime(totalTime);
    setRemainingTime(totalTime);
    setDotSpacing(params.dotSpacing);
    setCurrentWordIndex(0);
    
    // Start countdown
    setCountdown(3);
    const countdownInterval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownInterval);
          // Initialize dot position before starting (only if technique uses dot)
          const showDot = technique === 'dot' || technique === 'both';
          if (showDot && readingGuideRef.current && textContainerRef.current) {
            readingGuideRef.current.style.display = 'block';
            updateDotPosition(0);
          }
          setIsBlurred(false);
          setIsReading(true);
          return null;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const calculateReadingTime = (wordCount) => {
    return (wordCount / wpm) * 60 * 1000; // Convert to milliseconds
  };

  const formatTime = (ms) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const generateArticle = async () => {
    if (loading) return;
    setLoading(true);
    setError(null);
    setText('');
    setIsBlurred(true);
    setIsReading(false);
    
    try {
      const promptData = { 
        category,
        includeQuiz: true,
        language: articleLanguage,
        readingLevel
      };
      
      console.log('Sending prompt data:', promptData);
      
      const response = await api.post('/articles/generate', promptData);
      const data = response.data;

      if (!data.content) {
        throw new Error('No content received from server');
      }

      const words = data.content.split(/\s+/).filter(w => w.length > 0);
      setText(data.content);
      setWordCount(words.length);
      setIsBlurred(true);
      
      // Store difficult words for pronunciation guide
      if (data.difficultWords && data.difficultWords.length > 0) {
        setDifficultWords(data.difficultWords);
      } else {
        setDifficultWords([]);
      }

      // Check if quiz questions were included in the response
      if (data.quizQuestions && data.quizQuestions.length > 0) {
        console.log('Pre-generated quiz questions received:', data.quizQuestions.length);
        // Store quiz questions for later use
        setQuizQuestions(data.quizQuestions);
      } else {
        // Clear any existing quiz questions
        setQuizQuestions([]);
      }
      
      // Calculate optimal reading parameters
      const params = calculateReadingParams(data.content, wpm);
      setDotSpacing(params.dotSpacing);
      
      // Show estimated reading time
      const minutes = Math.floor(params.estimatedTime);
      const seconds = Math.round((params.estimatedTime - minutes) * 60);
      console.log(`Estimated reading time: ${minutes}m ${seconds}s at ${wpm} WPM`);
    } catch (error) {
      console.error('Error generating article:', error);
      const msg = error.response?.data?.error || error.message || 'Failed to generate article. Please try again.';
      setError(msg);
    }
    setLoading(false);
  };

  const updateDotPosition = (chunkStart) => {
    const wordElements = textContainerRef.current.querySelectorAll('.word');
    const containerRect = textContainerRef.current.getBoundingClientRect();
    const parentRect = textContainerRef.current.parentElement.getBoundingClientRect();
    
    // Dot goes in the CENTER of the chunk (between the two middle words)
    // For chunk size 2: between word[0] and word[1]
    // For chunk size 4: between word[1] and word[2]
    const midIndex = chunkStart + Math.floor(dotSpacing / 2);
    const wordBefore = wordElements[midIndex - 1]; // word to the left of dot
    const wordAfter = wordElements[midIndex];       // word to the right of dot
    
    // If either word is missing, fallback to edge of available word
    const targetWord = wordAfter || wordBefore;
    if (!targetWord || !textContainerRef.current.parentElement) return;
    
    const targetRect = (wordAfter || wordBefore).getBoundingClientRect();
    const verticalCenter = targetRect.top + (targetRect.height / 2) - containerRect.top - 2.5;
    
    let left;
    if (wordBefore && wordAfter) {
      const beforeRect = wordBefore.getBoundingClientRect();
      const afterRect = wordAfter.getBoundingClientRect();
      // Check if both words are on the same line
      if (Math.abs(beforeRect.top - afterRect.top) < afterRect.height * 0.5) {
        // Same line: center dot in the gap between them
        left = ((beforeRect.right + afterRect.left) / 2) - containerRect.left - 2.5;
      } else {
        // Different lines: dot should appear on the SAME line as the word AFTER it
        left = afterRect.left - containerRect.left - 8;
      }
    } else if (wordAfter) {
      // No word before (start of text) - place dot before first word
      const afterRect = wordAfter.getBoundingClientRect();
      left = afterRect.left - containerRect.left - 8;
    } else {
      // No word after (end of text) - place dot after last word
      const beforeRect = wordBefore.getBoundingClientRect();
      left = beforeRect.right - containerRect.left + 4;
    }
    const top = verticalCenter;

    // Update dot position
    if (readingGuideRef.current) {
      readingGuideRef.current.style.transform = `translate(${left}px, ${top}px)`;
    }

    // Auto-scroll: keep the active chunk visible
    const wordTopRelativeToViewport = targetRect.top;
    const wordBottomRelativeToViewport = targetRect.bottom;
    
    const parentTop = parentRect.top;
    const parentBottom = parentRect.bottom;
    const parentVisibleHeight = parentRect.height;
    
    const idealPosition = parentTop + (parentVisibleHeight * 0.4);
    
    const isTooHigh = wordTopRelativeToViewport < parentTop + (parentVisibleHeight * 0.3);
    const isTooLow = wordBottomRelativeToViewport > parentBottom - (parentVisibleHeight * 0.3);
    
    if ((isTooHigh || isTooLow) && !scrollAnimationRef.current) {
      const currentScrollTop = textContainerRef.current.parentElement.scrollTop;
      const targetScrollTop = currentScrollTop + (wordTopRelativeToViewport - idealPosition);
      
      if (Math.abs(targetScrollTop - currentScrollTop) < 5) return;

      if (isChrome) {
        if (scrollAnimationRef.current) {
          clearTimeout(scrollAnimationRef.current);
        }
        const scrollDistance = targetScrollTop - currentScrollTop;
        if (Math.abs(scrollDistance) > 5 && textContainerRef.current?.parentElement) {
          textContainerRef.current.parentElement.scrollTop = targetScrollTop;
        }
        scrollAnimationRef.current = null;
      } else {
        const startScrollPosition = currentScrollTop;
        const changeInScroll = targetScrollTop - startScrollPosition;
        const scrollDuration = 200;
        const startTime = performance.now();
        
        if (scrollAnimationRef.current) {
          cancelAnimationFrame(scrollAnimationRef.current);
        }
        
        const animateScroll = (currentTime) => {
          const elapsedTime = currentTime - startTime;
          const progress = Math.min(elapsedTime / scrollDuration, 1);
          
          const easeProgress = progress < 0.5
            ? 2 * progress * progress
            : -1 + (4 - 2 * progress) * progress;
          
          if (textContainerRef.current?.parentElement) {
            textContainerRef.current.parentElement.scrollTop = 
              startScrollPosition + (changeInScroll * easeProgress);
          }
            
          if (progress < 1) {
            scrollAnimationRef.current = requestAnimationFrame(animateScroll);
          } else {
            scrollAnimationRef.current = null;
          }
        };
        
        scrollAnimationRef.current = requestAnimationFrame(animateScroll);
      }
    }
  };

  const moveGuideDot = (words) => {
    if (!isReading || !words.length || !textContainerRef.current) return;

    const showDot = technique === 'dot' || technique === 'both';
    const totalWords = words.length;
    const msPerWord = 60000 / wpm;
    const msPerChunk = msPerWord * dotSpacing;
    const totalTimeMs = totalWords * msPerWord;
    
    let currentChunkIndex = 0;
    let startTime = null;
    let animationFrameId = null;

    // Initialize dot at the first word (only if showing dot)
    if (showDot && readingGuideRef.current) {
      readingGuideRef.current.style.display = 'block';
      updateDotPosition(0);
    }
    setCurrentWordIndex(0);

    const animate = (timestamp) => {
      if (!textContainerRef.current || !isReading) {
        if (animationFrameId) {
          cancelAnimationFrame(animationFrameId);
        }
        return;
      }

      if (!startTime) {
        startTime = timestamp;
      }

      const elapsedTime = timestamp - startTime;
      
      const remainingTime = Math.max(0, totalTimeMs - elapsedTime);
      setRemainingTime(remainingTime);

      // Calculate which chunk we should be on
      const expectedChunkStart = Math.min(
        Math.floor(elapsedTime / msPerChunk) * dotSpacing,
        totalWords - 1
      );

      // Only update when we move to a new chunk
      if (expectedChunkStart !== currentChunkIndex) {
        currentChunkIndex = expectedChunkStart;
        setCurrentWordIndex(currentChunkIndex);
        
        // Only move dot if technique includes it
        if (showDot && readingGuideRef.current) {
          updateDotPosition(currentChunkIndex);
        }
        
        // Check if we've reached the end
        if (currentChunkIndex + dotSpacing >= totalWords) {
          setTimeout(() => {
            setSessionCompleted(true);
            setIsReading(false);
          }, msPerChunk);
          return;
        }
      }

      animationFrameId = requestAnimationFrame(animate);
    };

    animationFrameId = requestAnimationFrame(animate);

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
      if (readingGuideRef.current) {
        readingGuideRef.current.style.display = 'none';
      }
      if (scrollAnimationRef.current) {
        cancelAnimationFrame(scrollAnimationRef.current);
        scrollAnimationRef.current = null;
      }
    };
  };

  const finishReading = async (quizScoreParam) => {
    try {
      // Calculate reading time in minutes
      const totalWords = text.split(/\s+/).filter(w => w.length > 0).length;
      const readingTimeMinutes = Math.round((totalWords / wpm) || 0);
      
      // Use the passed quiz score parameter if available, otherwise use the state value or default to 100
      const finalQuizScore = quizScoreParam !== undefined ? quizScoreParam : (quizScore || 100);
      
      console.log('Submitting reading session data:', {
        wpm,
        wordCount: totalWords,
        category,
        comprehensionScore: finalQuizScore,
        articleId: 'generated',
        readingTime: readingTimeMinutes
      });
      
      const response = await api.post('/usage/update-stats', {
        wpm,
        wordCount: totalWords,
        category,
        comprehensionScore: finalQuizScore,
        articleId: 'generated',
        readingTime: readingTimeMinutes,
        language: articleLanguage,
        readingLevel
      });

      if (response.data.badges) {
        // Convert badges object to array of earned badges
        const allBadges = Object.values(response.data.badges)
          .flatMap(category => Object.values(category))
          .filter(badge => badge.earned && badge.earnedDate);

        // Find newly earned badges (earned in this session)
        const now = new Date();
        const fiveMinutesAgo = new Date(now - 5 * 60 * 1000);
        const newBadges = allBadges.filter(badge => {
          const earnedDate = new Date(badge.earnedDate);
          return earnedDate >= fiveMinutesAgo && earnedDate <= now;
        });

        if (newBadges.length > 0) {
          setEarnedBadges(newBadges);
          setShowBadgeAlert(true);
          
          // Dispatch badge update event
          window.dispatchEvent(new Event('badgeUpdate'));
        }
      }

      if (response.data.flexCardEligibility) {
        setFlexCardEligibility(response.data.flexCardEligibility);
      }

      // Refresh the dashboard stats
      const statsResponse = await api.get('/usage/stats');
      if (statsResponse.data) {
        // Update local stats
        setCurrentStats(statsResponse.data);
      }

    } catch (error) {
      console.error('Error updating reading stats:', error);
      setError('Failed to update reading stats. Please try again.');
    }
  };

  const generateFallbackQuiz = (text) => {
    console.log('Generating fallback quiz for text length:', text.length);
    
    // Extract content from the text to create better questions
    const sentences = text.split(/[.!?]+/).filter(s => s.trim().length > 20);
    
    // Ensure we have at least 10 questions
    const questions = [];
    
    // Create more sophisticated content-based questions
    if (sentences.length >= 10) {
      // We have enough sentences to create good questions
      for (let i = 0; i < Math.min(sentences.length, 15); i++) {
        const sentence = sentences[i].trim();
        if (sentence.length < 20) continue; // Skip very short sentences
        
        // Create a more challenging question type based on the index
        const questionType = i % 5; // Use 5 different question types for more variety
        
        if (questionType === 0) {
          // Create a question about a specific detail in the text
          // Find nouns or important terms in the sentence
          const words = sentence.split(' ').filter(w => w.length > 4);
          if (words.length < 4) continue;
          
          // Select a key term that's not at the beginning or end of the sentence
          const keyWordIndex = Math.floor(words.length / 2);
          const keyWord = words[keyWordIndex];
          
          // Get a topic from the first few words of the sentence
          const topicWords = sentence.split(' ').slice(0, 3);
          const topic = topicWords.join(' ');
          
          // Create plausible but incorrect alternatives
          // Pull alternatives from other sentences to make them more plausible
          const alternatives = [];
          for (let j = 1; j < Math.min(sentences.length, 10); j++) {
            const otherSentence = sentences[(i + j) % sentences.length];
            const otherWords = otherSentence.split(' ').filter(w => w.length > 4);
            if (otherWords.length > 0) {
              const altWord = otherWords[Math.floor(Math.random() * otherWords.length)];
              if (altWord !== keyWord && !alternatives.includes(altWord) && altWord.length < 15) {
                alternatives.push(altWord);
              }
            }
          }
          
          // If we couldn't find enough alternatives, create some
          while (alternatives.length < 3) {
            const randomWord = words[Math.floor(Math.random() * words.length)];
            if (randomWord !== keyWord && !alternatives.includes(randomWord) && randomWord.length < 15) {
              alternatives.push(randomWord);
            }
          }
          
          // Create a complete question with specific topic reference
          questions.push({
            type: "multiple_choice",
            difficulty: "medium",
            skill: "detail",
            question: `What term is specifically mentioned in relation to ${topic}?`,
            options: [
              { text: keyWord, isCorrect: true },
              { text: alternatives[0], isCorrect: false },
              { text: alternatives[1], isCorrect: false },
              { text: alternatives[2], isCorrect: false }
            ]
          });
        } else if (questionType === 1) {
          // Create a question about the main topic of the sentence
          const mainTopicWords = sentence.split(' ').slice(0, 4);
          const mainTopic = mainTopicWords.join(' ');
          
          // Create options with the correct topic and three incorrect topics from other sentences
          const incorrectTopics = [];
          for (let j = 1; j < Math.min(sentences.length, 10); j++) {
            const otherSentence = sentences[(i + j) % sentences.length];
            if (otherSentence.length > 30) {
              const otherTopic = otherSentence.split(' ').slice(0, 4).join(' ');
              if (!incorrectTopics.includes(otherTopic) && otherTopic !== mainTopic) {
                incorrectTopics.push(otherTopic);
                if (incorrectTopics.length === 3) break;
              }
            }
          }
          
          // If we couldn't find enough alternatives, create generic ones
          while (incorrectTopics.length < 3) {
            incorrectTopics.push(`An unrelated topic not mentioned in the text`);
          }
          
          questions.push({
            type: "multiple_choice",
            difficulty: "easy",
            skill: "main_idea",
            question: `Which of the following is a main topic discussed in the text?`,
            options: [
              { text: mainTopic, isCorrect: true },
              { text: incorrectTopics[0], isCorrect: false },
              { text: incorrectTopics[1], isCorrect: false },
              { text: incorrectTopics[2], isCorrect: false }
            ]
          });
        } else if (questionType === 2) {
          // Create a question about the relationship between concepts
          // Find two sentences that might be related
          const nextSentence = sentences[(i + 1) % sentences.length].trim();
          
          // Get topics from both sentences
          const topic1 = sentence.split(' ').slice(0, 3).join(' ');
          const topic2 = nextSentence.split(' ').slice(0, 3).join(' ');
          
          // Create a question about the relationship with specific topics
          questions.push({
            type: "multiple_choice",
            difficulty: "hard",
            skill: "relationship",
            question: `What is the relationship between "${topic1}" and "${topic2}" in the text?`,
            options: [
              { text: "They are related concepts discussed sequentially.", isCorrect: true },
              { text: "They present contradictory viewpoints.", isCorrect: false },
              { text: "They are unrelated topics in different sections.", isCorrect: false },
              { text: "The second concept disproves the first.", isCorrect: false }
            ]
          });
        } else if (questionType === 3) {
          // Create a question about a specific fact or detail
          // Find a specific fact mentioned in the text
          const factWords = sentence.split(' ');
          const topic = factWords.slice(0, 3).join(' ');
          
          // Create a question about this fact
          questions.push({
            type: "multiple_choice",
            difficulty: "easy",
            skill: "comprehension",
            question: `Which statement about ${topic} is accurate according to the text?`,
            options: [
              { text: sentence, isCorrect: true },
              { text: `${factWords.slice(0, factWords.length/2).join(' ')} is not ${factWords.slice(factWords.length/2).join(' ')}`, isCorrect: false },
              { text: `The opposite of what is stated about ${topic}.`, isCorrect: false },
              { text: `${topic} is not mentioned in the text.`, isCorrect: false }
            ]
          });
        } else if (questionType === 4) {
          // Create a vocabulary-in-context question
          // Find a longer word (likely more complex) in the sentence
          const words = sentence.split(' ').filter(w => w.length > 6);
          if (words.length === 0) continue;
          
          const targetWord = words[Math.floor(Math.random() * words.length)];
          const contextPhrase = sentence.substring(
            Math.max(0, sentence.indexOf(targetWord) - 20),
            Math.min(sentence.length, sentence.indexOf(targetWord) + targetWord.length + 20)
          );
          
          questions.push({
            type: "multiple_choice",
            difficulty: "medium",
            skill: "vocabulary",
            question: `What is the meaning of "${targetWord}" as used in the text?`,
            options: [
              { text: "The correct meaning based on context.", isCorrect: true },
              { text: "An incorrect but plausible alternative meaning.", isCorrect: false },
              { text: "A meaning that contradicts the context.", isCorrect: false },
              { text: "A meaning unrelated to the text.", isCorrect: false }
            ]
          });
        }
        
        // If we have 10 questions, we can stop
        if (questions.length >= 10) break;
      }
    }
    
    // If we still don't have enough questions, add some generic ones based on paragraphs
    if (questions.length < 10) {
      const paragraphs = text.split('\n\n').filter(p => p.trim().length > 50);
      
      for (let i = 0; i < Math.min(paragraphs.length, 10 - questions.length); i++) {
        const paragraph = paragraphs[i].trim();
        
        // Extract a topic from the paragraph
        const topic = paragraph.split(' ').slice(0, 5).join(' ');
        
        // Create a question about this paragraph with a specific topic
        questions.push({
          type: "multiple_choice",
          difficulty: "medium",
          skill: "main_idea",
          question: `What is the main point about ${topic} in the text?`,
          options: [
            { text: paragraph.substring(0, 75) + "...", isCorrect: true },
            { text: `The opposite of what is stated in the text.`, isCorrect: false },
            { text: `A point not directly addressed in the text.`, isCorrect: false },
            { text: `A contradictory viewpoint to the one presented.`, isCorrect: false }
          ]
        });
      }
    }
    
    // If we still don't have enough questions, add some very generic ones
    while (questions.length < 10) {
      const randomIndex = Math.floor(Math.random() * sentences.length);
      const randomSentence = sentences[randomIndex].trim();
      const topic = randomSentence.split(' ').slice(0, 3).join(' ');
      
      questions.push({
        type: "multiple_choice",
        difficulty: "medium",
        skill: "main_idea",
        question: `Which statement about ${topic} is supported by the text?`,
        options: [
          { text: randomSentence.substring(0, 75) + "...", isCorrect: true },
          { text: "The text does not discuss this topic.", isCorrect: false },
          { text: "The opposite is stated in the text.", isCorrect: false },
          { text: "This is mentioned as a possibility but not confirmed.", isCorrect: false }
        ]
      });
    }
    
    // Ensure all questions have proper punctuation
    questions.forEach(q => {
      // Add a question mark if the question doesn't end with one
      if (!q.question.endsWith('?')) {
        q.question += '?';
      }
      
      // Ensure options have proper punctuation
      q.options.forEach(opt => {
        if (!opt.text.endsWith('.') && !opt.text.endsWith('?') && !opt.text.endsWith('!') && !opt.text.endsWith('...')) {
          opt.text += '.';
        }
      });
    });
    
    return questions.slice(0, 10); // Ensure we return exactly 10 questions
  };

  const startQuiz = async () => {
    try {
      if (!text || text.trim().length === 0) {
        throw new Error('No text content available for quiz generation');
      }

      // Enhanced logging for debugging quiz generation
      console.log('Starting quiz generation process');
      console.log('Text length for quiz generation:', text.length);
      
      // Check if we already have quiz questions from article generation
      if (quizQuestions && quizQuestions.length > 0) {
        console.log('Using pre-generated quiz questions:', quizQuestions.length);
        setQuizInProgress(true);
        setCurrentQuizQuestion(0);
        setQuizAnswers(new Array(quizQuestions.length).fill(null));
        return;
      }
      
      // If no pre-generated questions, request them from the server
      setLoading(true);
      
      console.log('Sending quiz request to /quiz/reading-comprehension');
      const response = await api.post('/quiz/reading-comprehension', {
        text
      });
      
      console.log('Quiz API response received:', response);
      
      if (response.data && response.data.questions) {
        console.log('Quiz questions received:', response.data.questions.length);
        setQuizQuestions(response.data.questions);
        setQuizInProgress(true);
        setCurrentQuizQuestion(0);
        setQuizAnswers(new Array(response.data.questions.length).fill(null));
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error generating quiz:', error);
      setLoading(false);
      setError('Failed to generate quiz questions. Using fallback questions instead.');
      
      // Use fallback quiz questions instead of skipping the quiz
      const fallbackQuestions = generateFallbackQuiz(text);
      console.log('Using fallback quiz questions:', fallbackQuestions.length);
      
      setQuizQuestions(fallbackQuestions);
      setQuizInProgress(true);
      setCurrentQuizQuestion(0);
      setQuizAnswers(new Array(fallbackQuestions.length).fill(null));
    }
  };

  const handleAnswerSelect = (answerIndex) => {
    // Update answers array
    const newAnswers = [...quizAnswers];
    newAnswers[currentQuizQuestion] = answerIndex;
    setQuizAnswers(newAnswers);
    
    // Move to next question or finish quiz
    if (currentQuizQuestion < quizQuestions.length - 1) {
      setCurrentQuizQuestion(currentQuizQuestion + 1);
    } else {
      completeQuiz(newAnswers);
    }
  };

  const completeQuiz = (answers) => {
    // Calculate score
    const correctAnswers = quizQuestions.reduce((count, question, index) => {
      const correctOptionIndex = question.options.findIndex(opt => opt.isCorrect);
      return answers[index] === correctOptionIndex ? count + 1 : count;
    }, 0);
    
    const score = Math.round((correctAnswers / quizQuestions.length) * 100);
    console.log('Quiz completed with score:', score, `(${correctAnswers}/${quizQuestions.length} correct)`);
    
    setQuizScore(score);
    setQuizCompleted(true);
    setQuizInProgress(false);
    
    // Now finish the reading session with the quiz score
    finishReading(score);
  };

  const renderQuizQuestion = () => {
    if (!quizInProgress || quizQuestions.length === 0) return null;
    
    const question = quizQuestions[currentQuizQuestion];
    
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Question {currentQuizQuestion + 1} of {quizQuestions.length}
        </Typography>
        <Paper 
          elevation={2} 
          sx={{ 
            p: 3, 
            mb: 3, 
            backgroundColor: 'primary.light', 
            color: 'primary.contrastText',
            borderRadius: 2
          }}
        >
          <Typography 
            variant="body1" 
            sx={{ 
              mb: 2, 
              overflowWrap: 'break-word', 
              wordBreak: 'break-word',
              whiteSpace: 'pre-line',
              maxWidth: '100%',
              fontWeight: 'medium',
              fontSize: '1.1rem',
              lineHeight: 1.5
            }}
          >
            {question.question}
          </Typography>
          {question.difficulty && (
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <Typography variant="caption" sx={{ 
                bgcolor: question.difficulty === 'easy' ? 'success.main' : 
                        question.difficulty === 'medium' ? 'warning.main' : 'error.main',
                px: 1,
                py: 0.5,
                borderRadius: 1,
                color: '#fff',
                fontWeight: 'bold'
              }}>
                {question.difficulty.toUpperCase()}
              </Typography>
              {question.skill && (
                <Typography variant="caption" sx={{ 
                  bgcolor: 'info.main',
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  color: '#fff',
                  fontWeight: 'bold'
                }}>
                  {question.skill.replace('_', ' ').toUpperCase()}
                </Typography>
              )}
            </Box>
          )}
        </Paper>
        
        <Stack spacing={2} sx={{ width: '100%' }}>
          {question.options.map((option, index) => (
            <Button
              key={index}
              variant="outlined"
              color="primary"
              onClick={() => handleAnswerSelect(index)}
              sx={{ 
                justifyContent: 'flex-start', 
                textAlign: 'left',
                p: 2,
                overflowWrap: 'break-word',
                wordBreak: 'break-word',
                whiteSpace: 'normal',
                lineHeight: 1.5,
                width: '100%',
                minHeight: '60px',
                borderWidth: '2px',
                '&:hover': {
                  borderWidth: '2px',
                  backgroundColor: 'rgba(33, 150, 243, 0.08)'
                }
              }}
            >
              <Typography 
                variant="body1" 
                sx={{ 
                  overflowWrap: 'break-word', 
                  wordBreak: 'break-word',
                  width: '100%'
                }}
              >
                {option.text}
              </Typography>
            </Button>
          ))}
        </Stack>
      </Box>
    );
  };

  const renderQuizResults = () => {
    const isPassed = quizScore >= 80;
    
    return (
      <Box sx={{ mt: 4, mb: 4, maxHeight: '70vh', overflow: 'auto', px: 2 }}>
        <Typography variant="h5" gutterBottom>
          Quiz Results
        </Typography>
        <Paper 
          elevation={3} 
          sx={{ 
            p: 3, 
            mb: 4, 
            backgroundColor: isPassed ? 'success.light' : 'error.light',
            color: isPassed ? 'success.dark' : 'error.dark',
            borderRadius: 2
          }}
        >
          <Typography variant="h6" gutterBottom>
            Score: {quizScore}% - {isPassed ? 'Passed' : 'Needs Improvement'}
          </Typography>
          <Typography variant="body2">
            {isPassed 
              ? 'Great job! You demonstrated good comprehension of the reading material.' 
              : 'You might want to review the material again to improve your understanding.'}
          </Typography>
        </Paper>
        
        <List sx={{ mb: 3 }}>
          {quizQuestions.map((question, index) => (
            <ListItem 
              key={index} 
              sx={{ 
                flexDirection: 'column', 
                alignItems: 'flex-start', 
                mb: 3, 
                p: 3, 
                bgcolor: 'background.paper', 
                borderRadius: 2,
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
            >
              <Typography 
                variant="subtitle1" 
                gutterBottom 
                sx={{ 
                  mb: 2, 
                  fontWeight: 'bold', 
                  overflowWrap: 'break-word', 
                  wordBreak: 'break-word',
                  width: '100%'
                }}
              >
                <strong>Question {index + 1}:</strong> {question.question}
              </Typography>
              <Box sx={{ width: '100%' }}>
                {question.options.map((option, optIndex) => {
                  const isSelected = quizAnswers[index] === optIndex;
                  const isCorrect = option.isCorrect;
                  
                  let bgColor = 'transparent';
                  let borderColor = 'divider';
                  
                  if (isSelected && isCorrect) {
                    bgColor = 'success.light';
                    borderColor = 'success.main';
                  } else if (isSelected && !isCorrect) {
                    bgColor = 'error.light';
                    borderColor = 'error.main';
                  } else if (!isSelected && isCorrect) {
                    bgColor = 'success.light';
                    borderColor = 'success.main';
                  }
                  
                  return (
                    <Box 
                      key={optIndex} 
                      sx={{ 
                        p: 2, 
                        mb: 1.5, 
                        bgcolor: bgColor,
                        border: '1px solid',
                        borderColor: borderColor,
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'flex-start',
                        transition: 'all 0.2s ease',
                        overflowWrap: 'break-word',
                        wordBreak: 'break-word',
                        whiteSpace: 'normal',
                        lineHeight: 1.5
                      }}
                    >
                      <Box sx={{ mr: 2, mt: 0.5, flexShrink: 0 }}>
                        {isSelected && isCorrect && <CheckCircleIcon color="success" />}
                        {isSelected && !isCorrect && <CancelIcon color="error" />}
                        {!isSelected && isCorrect && <CheckCircleOutlineIcon color="success" />}
                        {!isSelected && !isCorrect && <RadioButtonUncheckedIcon color="disabled" />}
                      </Box>
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          overflowWrap: 'break-word', 
                          wordBreak: 'break-word',
                          flex: 1
                        }}
                      >
                        {option.text}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
              {question.difficulty && (
                <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                  <Typography variant="caption" sx={{ 
                    bgcolor: question.difficulty === 'easy' ? 'success.light' : 
                             question.difficulty === 'medium' ? 'warning.light' : 'error.light',
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    color: question.difficulty === 'easy' ? 'success.dark' : 
                           question.difficulty === 'medium' ? 'warning.dark' : 'error.dark',
                  }}>
                    {question.difficulty.toUpperCase()}
                  </Typography>
                  {question.skill && (
                    <Typography variant="caption" sx={{ 
                      bgcolor: 'info.light',
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      color: 'info.dark'
                    }}>
                      {question.skill.replace('_', ' ').toUpperCase()}
                    </Typography>
                  )}
                </Box>
              )}
            </ListItem>
          ))}
        </List>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={() => setShowStats(true)} 
            sx={{ px: 3, py: 1.5 }}
            startIcon={<AssessmentIcon />}
          >
            View Reading Stats
          </Button>
          <Button 
            variant="outlined"
            onClick={() => {
              setIsBlurred(true);
              setText('');
              setQuizQuestions([]);
              setQuizCompleted(false);
              setQuizInProgress(false);
              setShowStats(false);
              setCurrentWordIndex(0);
            }}
            sx={{ px: 3, py: 1.5 }}
            startIcon={<RefreshIcon />}
          >
            Start New Session
          </Button>
        </Box>
      </Box>
    );
  };

  const renderText = () => {
    if (!text) return null;
    
    // Split text into words and handle multiple spaces
    const words = text.split(/\s+/).filter(word => word.length > 0);
    
    // Highlight is anchored to chunk boundaries (only when chunking is enabled)
    const showHighlight = technique === 'chunk' || technique === 'both';
    const chunkStart = (isReading && showHighlight) ? Math.floor(currentWordIndex / dotSpacing) * dotSpacing : -1;
    
    return words.map((word, index, array) => {
      const isActive = isReading && showHighlight && index >= chunkStart && index < chunkStart + dotSpacing;
      
      return (
        <React.Fragment key={index}>
          <span 
            className="word" 
            style={{
              color: isActive ? '#00BFFF' : 'inherit'
            }}
          >
            {word}
          </span>
          {index < array.length - 1 && <span className="space">{' '}</span>}
        </React.Fragment>
      );
    });
  };

  const categories = [
    { value: 'general', label: 'General Knowledge' },
    { value: 'science', label: 'Science & Nature' },
    { value: 'history', label: 'History & Civilization' },
    { value: 'technology', label: 'Technology & Computing' },
    { value: 'literature', label: 'Literature & Arts' },
    { value: 'psychology', label: 'Psychology & Behavior' },
    { value: 'philosophy', label: 'Philosophy & Ideas' },
    { value: 'business', label: 'Business & Economics' },
    { value: 'health', label: 'Health & Medicine' },
    { value: 'space', label: 'Space & Astronomy' },
    { value: 'environment', label: 'Environment & Climate' },
    { value: 'politics', label: 'Politics & Society' },
    { value: 'sports', label: 'Sports & Athletics' },
    { value: 'food', label: 'Food & Cuisine' },
    { value: 'travel', label: 'Travel & Culture' }
  ];

  const techniques = [
    { value: 'both', label: 'Guide Dot + Chunking' },
    { value: 'dot', label: 'Guide Dot Only' },
    { value: 'chunk', label: 'Chunking Only' }
  ];

  useEffect(() => {
    if (text) {
      const params = calculateReadingParams(text, wpm);
      setDotSpacing(params.dotSpacing);
    }
  }, [wpm, text, technique]);

  useEffect(() => {
    // Snap dotSpacing to valid even value when switching to 'both'
    if (technique === 'both' && dotSpacing !== 2 && dotSpacing !== 4) {
      setDotSpacing(dotSpacing <= 3 ? 2 : 4);
    }
  }, [technique]);

  useEffect(() => {
    let cleanup = null;
    
    if (isReading && text) {
      const words = text.split(/\s+/).filter(w => w.length > 0);
      console.log('Starting reading session:', {
        words: words.length,
        wpm,
        dotSpacing,
        estimatedTime: Math.round((words.length / wpm) || 0)
      });
      cleanup = moveGuideDot(words);
    }

    return () => {
      if (cleanup) cleanup();
    };
  }, [isReading, text, wpm, dotSpacing]);

  useEffect(() => {
    if (sessionCompleted && currentWordIndex > 0) {
      console.log('Reading session completed, starting quiz:', {
        wpm,
        wordCount,
        category,
        progress: `${currentWordIndex}/${wordCount} words`,
        textLength: text ? text.length : 0,
        hasText: Boolean(text),
        completionTime: new Date().toISOString()
      });
      
      // Add a small delay before starting the quiz to ensure UI updates properly
      setTimeout(() => {
        startQuiz();
        setSessionCompleted(false);
      }, 500);
    }
  }, [sessionCompleted, currentWordIndex, wpm, wordCount, category, text]);

  const renderReadingStats = () => {
    return (
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Reading Session Stats
        </Typography>
        
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Current Session
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Reading Speed</Typography>
              <Typography variant="body1">{wpm} WPM</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Words Read</Typography>
              <Typography variant="body1">{wordCount}</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Reading Time</Typography>
              <Typography variant="body1">{Math.round((wordCount / wpm) || 0)} minutes</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Comprehension Score</Typography>
              <Typography variant="body1">{quizScore}%</Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">Category</Typography>
              <Typography variant="body1" sx={{ textTransform: 'capitalize' }}>{category}</Typography>
            </Box>
          </Box>
        </Paper>
        
        {currentStats && (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Overall Progress
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Total Words Read</Typography>
                <Typography variant="body1">{currentStats.totalWordsRead || 0}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Total Reading Time</Typography>
                <Typography variant="body1">{currentStats.totalReadingTime || 0} minutes</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Average Reading Speed</Typography>
                <Typography variant="body1">{currentStats.averageWpm || 0} WPM</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Average Comprehension</Typography>
                <Typography variant="body1">{currentStats.averageComprehension || 0}%</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Sessions Completed</Typography>
                <Typography variant="body1">{currentStats.sessionsCompleted || 0}</Typography>
              </Box>
            </Box>
          </Paper>
        )}
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
          <Button 
            variant="outlined" 
            onClick={() => {
              setShowStats(false);
              setQuizCompleted(false);
            }}
          >
            Back to Quiz Results
          </Button>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={() => {
              setIsBlurred(true);
              setText('');
              setQuizQuestions([]);
              setQuizCompleted(false);
              setQuizInProgress(false);
              setShowStats(false);
              setCurrentWordIndex(0);
            }}
          >
            Start New Session
          </Button>
        </Box>
      </Box>
    );
  };

  return (
    <Container maxWidth="md">
      <FlexCardPrompt
        eligibility={flexCardEligibility}
        sessionId={flexCardEligibility?.sessionId}
      />
      <Snackbar 
        open={showBadgeAlert} 
        autoHideDuration={6000} 
        onClose={() => setShowBadgeAlert(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setShowBadgeAlert(false)} severity="success">
          Congratulations! You've earned new badges!
        </Alert>
      </Snackbar>

      <Box sx={{ py: 4 }}>
        <Typography variant="h4" gutterBottom>
          Speed Reading Practice
        </Typography>

        <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 2 }}>
            <FormControl fullWidth sx={{ '& .MuiSelect-select': { fontSize: { xs: '16px', sm: 'inherit' } } }}>
              <InputLabel>Category</InputLabel>
              <Select label="Category" value={category} onChange={(e) => setCategory(e.target.value)}>
                {categories.map((cat) => (
                  <MenuItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ '& .MuiSelect-select': { fontSize: { xs: '16px', sm: 'inherit' } } }}>
              <InputLabel>Language</InputLabel>
              <Select label="Language" value={articleLanguage} onChange={(e) => setArticleLanguage(e.target.value)}>
                {languages.map((lang) => (
                  <MenuItem key={lang.value} value={lang.value}>
                    {lang.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 2 }}>
            <FormControl fullWidth sx={{ '& .MuiSelect-select': { fontSize: { xs: '16px', sm: 'inherit' } } }}>
              <InputLabel>Reading Level</InputLabel>
              <Select label="Reading Level" value={readingLevel} onChange={(e) => setReadingLevel(e.target.value)}>
                {readingLevels.map((lvl) => (
                  <MenuItem key={lvl.value} value={lvl.value}>
                    {lvl.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="contained"
              onClick={generateArticle}
              disabled={loading}
              sx={{ minWidth: { xs: '100%', sm: '200px' }, minHeight: 44 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Generate Article'}
            </Button>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 2 }}>
            <FormControl fullWidth sx={{ '& .MuiSelect-select': { fontSize: { xs: '16px', sm: 'inherit' } } }}>
              <InputLabel>Reading Technique</InputLabel>
              <Select label="Reading Technique" value={technique} onChange={(e) => setTechnique(e.target.value)}>
                {techniques.map((t) => (
                  <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              type="number"
              label="Words Per Minute"
              value={wpm}
              onChange={(e) => {
                const val = Math.max(50, Math.min(3000, Number(e.target.value)));
                if (!isNaN(val)) setWpm(val);
              }}
              InputProps={{
                endAdornment: <InputAdornment position="end">WPM</InputAdornment>,
              }}
              sx={{ minWidth: { xs: '100%', sm: '150px' }, '& .MuiInputBase-input': { fontSize: { xs: '16px', sm: 'inherit' } } }}
            />

            {technique === 'both' ? (
              <FormControl sx={{ minWidth: { xs: '100%', sm: '150px' }, '& .MuiSelect-select': { fontSize: { xs: '16px', sm: 'inherit' } } }}>
                <InputLabel>Chunk Size</InputLabel>
                <Select
                  label="Chunk Size"
                  value={dotSpacing}
                  onChange={(e) => setDotSpacing(Number(e.target.value))}
                >
                  <MenuItem value={2}>2 words</MenuItem>
                  <MenuItem value={4}>4 words</MenuItem>
                </Select>
              </FormControl>
            ) : (
              <TextField
                type="number"
                label="Chunk Size"
                value={dotSpacing}
                onChange={(e) => {
                  const val = Math.max(1, Math.min(10, Number(e.target.value)));
                  if (!isNaN(val)) setDotSpacing(val);
                }}
                sx={{ minWidth: { xs: '100%', sm: '150px' }, '& .MuiInputBase-input': { fontSize: { xs: '16px', sm: 'inherit' } } }}
              />
            )}
          </Box>

          <Box sx={{ px: { xs: 0, sm: 2 } }}>
            <Typography gutterBottom>Reading Speed: {wpm} WPM</Typography>
            <Slider
              value={wpm}
              onChange={(e, newValue) => setWpm(newValue)}
              min={100}
              max={2500}
              step={50}
              marks={[
                { value: 100, label: '100' },
                { value: 500, label: '500' },
                { value: 1000, label: '1000' },
                { value: 1500, label: '1500' },
                { value: 2000, label: '2000' },
                { value: 2500, label: '2500' }
              ]}
            />
          </Box>
        </Paper>

        {text && (
          <InfoBar>
            <Typography variant="body1">
              Word Count: <strong>{wordCount}</strong>
            </Typography>
            {isReading && (
              <Typography variant="body1">
                Time Remaining: <strong>{formatTime(remainingTime)}</strong>
              </Typography>
            )}
          </InfoBar>
        )}
        
        <Box sx={{ 
          position: 'relative', 
          overflow: 'hidden', 
          maxHeight: '60vh',
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
          scrollBehavior: 'smooth',
          willChange: 'scroll-position'
        }}>
          {!quizInProgress && !quizCompleted ? (
            <BlurredText isBlurred={isBlurred} ref={textContainerRef.current?.parentElement ? textContainerRef.current.parentElement : null}>
              <TextContainer ref={textContainerRef}>
                {renderText()}
                <ReadingGuide 
                  ref={readingGuideRef}
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    transform: 'translate(0, 0)',
                    display: (isReading && (technique === 'dot' || technique === 'both')) ? 'block' : 'none'
                  }}
                />
              </TextContainer>
            </BlurredText>
          ) : quizInProgress ? (
            renderQuizQuestion()
          ) : (
            renderQuizResults()
          )}
        </Box>

        {countdown !== null && (
          <CountdownOverlay>
            <Typography variant="h1">{countdown}</Typography>
          </CountdownOverlay>
        )}

        {showStats && (
          renderReadingStats()
        )}

        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          {!text || quizInProgress || quizCompleted ? null : (
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={startReading}
              disabled={!text || isReading}
            >
              Start Reading
            </Button>
          )}
        </Box>

        {/* Pronunciation Guide — subtle, non-disruptive */}
        {difficultWords.length > 0 && !isReading && !quizInProgress && text && (
          <Box sx={{ mt: 3 }}>
            <Box
              onClick={() => setSelectedWord(selectedWord === 'panel' ? null : 'panel')}
              sx={{
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 1.5,
                borderRadius: 2,
                backgroundColor: 'rgba(0, 191, 255, 0.06)',
                border: '1px solid rgba(0, 191, 255, 0.2)',
                transition: 'all 0.2s',
                '&:hover': { backgroundColor: 'rgba(0, 191, 255, 0.1)' }
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                📖 Pronunciation Guide ({difficultWords.length} words)
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                {selectedWord === 'panel' ? 'Click to hide' : 'Click to expand'}
              </Typography>
            </Box>
            {selectedWord === 'panel' && (
              <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                {difficultWords.map((dw, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      backgroundColor: 'background.paper',
                      minWidth: 160,
                      flex: '1 1 200px',
                      maxWidth: 300
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight="bold">
                      {dw.word}
                    </Typography>
                    {dw.phonetic && (
                      <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', display: 'block' }}>
                        /{dw.phonetic}/
                      </Typography>
                    )}
                    {dw.romanization && (
                      <Typography variant="caption" color="info.main" sx={{ display: 'block' }}>
                        {dw.romanization}
                      </Typography>
                    )}
                    {dw.definition && (
                      <Typography variant="body2" sx={{ mt: 0.5, fontSize: '0.8rem' }}>
                        {dw.definition}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default SpeedReader;
